/* eslint-disable import/no-cycle */
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core'

import { map } from 'rxjs'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import {
  CollectionTemplateConfig,
  collectionTemplateConfig$,
  prototypeInstanceMetaToPath,
} from '@flowx/core-sdk'

import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { getBoolExpressionSignal } from '../helpers'

// As a path, so an equal-but-fresh index chain compares equal.
const metaPath = (config: CollectionTemplateConfig): string | undefined =>
  config.prototypeInstanceMeta && prototypeInstanceMetaToPath(config.prototypeInstanceMeta)

@Component({
  selector: 'flx-collection',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [CommonModule, FlxTreeNodeComponent, FlxThemeModule, FlxStyleModule],
  templateUrl: './flx-collection.component.html',
  styleUrl: './flx-collection.component.scss',
  host: {
    '[class]': `displayOptions().className`,
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxCollectionComponent implements OnInit, AfterViewInit {
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)
  // A nested collection's templateConfig churns per outer emission. The async pipe resubscribes
  // whenever it is handed a new observable, and a resubscribe reseeds the expansion's reuse state,
  // so what feeds the expansion may only move on the scalars the expansion actually reads.
  // Prototype children compare by reference on purpose: an ingest that merges children into a
  // prototype must resubscribe, or the closure keeps rendering the pre-merge prototypes forever.
  #expansionConfig = computed(() => this.templateConfig(), {
    equal: (a, b) =>
      a.processInstanceUuid === b.processInstanceUuid &&
      a.flowxUuid === b.flowxUuid &&
      a.displayOptions.flowxProps.collectionSource ===
        b.displayOptions.flowxProps.collectionSource &&
      a.templateConfig === b.templateConfig &&
      metaPath(a) === metaPath(b),
  })
  templateConfig = input.required<CollectionTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  collectionDataPath = computed(() => this.flowxProps().collectionSource)

  collectionTemplateConfig = computed(() =>
    collectionTemplateConfig$(this.#expansionConfig()).pipe(
      map((config) => ({
        ...config,
        rootTemplateConfig: { ...untracked(this.templateConfig), ...config.rootTemplateConfig },
      }))
    )
  )

  // expressions
  isHidden: Signal<boolean>

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const { hide } = this.flowxProps()
      this.isHidden = getBoolExpressionSignal(hide, this.templateConfig(), { logType: 'hide' })

      const { conditionals } = this.style()
      if (Array.isArray(conditionals)) {
        this.evaluatedConditionalStyles.set(
          conditionals.map(({ condition, overrides }, index) => ({
            expression: getBoolExpressionSignal(condition, this.templateConfig(), {
              logType: 'conditional',
              conditionIndex: index,
            }),
            overrides,
          }))
        )
      }
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
